/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics as commonAnalytics,
    utils as commonUtils,
} from '@sitebuilder/common';
import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import titledSectionStyles, { themetitledSectionStyles } from './TitledSection.styles';

const CHILDREN_PLACEHOLDER_ID = 'children';

type Switch = 'on' | 'off';

interface ToggleSection {
    id: string;
    label?: string;
}

const defaultToggleSections = [
    { id: 'section1', label: '' },
    { id: 'section2', label: '' },
];

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}
export interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    id?: string;
    title?: string;
    isCollapsible?: boolean;
    showIcon?: boolean;
    defaultCollpsible?: string;
    iconShowContent?: string;
    iconHideContent?: string;
    iconPosition?: string;
    toggleSection?: boolean;
    toggleLabels?: string;
    analyticsLabels?: string;
    titles?: Headings[];
    defaultActiveSection: Switch;
    functionToTransform?: string;
    isTransformData?: boolean;
    showToolTip?: boolean;
    tooltipOptions: { backgroundColor?: string; text?: string; iconName: string };
    typographyClass?: string;
    disableTabStaticData?: boolean;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    useLowerCase?: boolean;
}

const TitledSection = (props: commonTypes.WidgetInstanceProps): JSX.Element => {
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        id,
        title,
        isCollapsible,
        showIcon,
        defaultCollpsible,
        iconShowContent,
        iconHideContent,
        iconPosition,
        showToolTip,
        tooltipOptions,
        useAnalytics,
        dynamicEvents,
        useLowerCase,
        ...rest
    } = widgetProperties || props;

    const { Placeholder } = commonHooks.useAppContext();
    const theme = props?.data?.themeSelected || props?.theme;

    const i18n = commonHooks.useLocalization();
    const tracking = commonAnalytics.useTracking();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [showSection, setShowSection] = useState<boolean>(
        defaultCollpsible ? defaultCollpsible === 'Show' : true
    );

    const [toggleSwitch, setToggleSwitch] = useState<Switch>(rest.defaultActiveSection || 'on');
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const { data } = props;

    const compiledLabels = rest.toggleLabels && i18n.format(rest.toggleLabels || '', data);
    let isRender = {
        titles: true,
        children: true,
        titledSection: true,
        hideTitlesMsg: '',
        hideChildrenMsg: '',
        hideTitledSectionMsg: '',
        hideSections: [],
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            rest.isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, rest.functionToTransform, {
                ...data,
            });

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    // Toggle section
    let sections = compiledLabels
        ? defaultToggleSections.map((s: ToggleSection, i: number) => ({
              ...s,
              label: compiledLabels?.split(',')[i],
          }))
        : defaultToggleSections;

    if (isRender.hideSections.length) {
        sections = sections.filter(
            (_, i: number) => !(isRender.hideSections as number[]).includes(i)
        );
    }

    const analyticsLabels = rest.analyticsLabels?.split(',');

    const activeSection = toggleSwitch === 'on' ? sections[0] : sections[1];

    // get title from data
    // todo: @hamid
    const titleHtml = i18n.format(title || '', data);
    const tooltipText = i18n.format(tooltipOptions?.text || '', data);

    // Render Icon according to icon position
    const renderIcon = (position: string) => (
        <button
            className={`icon icon-${position}`}
            tabIndex={0}
            aria-label={
                showSection
                    ? `${i18n.translate('widgets_expand')} ${titleHtml}`
                    : `${i18n.translate('widgets_collapse')} ${titleHtml}`
            }
            aria-expanded={showSection}
        >
            <i className={showSection ? iconHideContent : iconShowContent} />
        </button>
    );

    const getConditionalProps = (isCollapsible?: boolean) => {
        if (isCollapsible) {
            return {
                onClick: () => {
                    isCollapsible && setShowSection(!showSection);
                },
            };
        }
    };

    return (
        <>
            {isRender.titledSection ? (
                <section
                    id={id}
                    className={`section section--titled ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${!theme
                            ? titledSectionStyles
                            : themetitledSectionStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    {isRender.titles ? (
                        <div
                            className={`section-title ${data?.locale === 'fr-CA' ? 'french' : ''}`}
                            {...getConditionalProps(isCollapsible)}
                        >
                            {showIcon && iconPosition === 'Left' && renderIcon('left')}
                            {titleHtml && <h2 dangerouslySetInnerHTML={{ __html: titleHtml }} />}
                            <HeaderWrapper
                                headings={rest.titles}
                                data={data}
                                disableTabStaticData={
                                    globalDisableTabStaticData || rest.disableTabStaticData
                                }
                            />

                            {showToolTip && (
                                <div className="tooltip-container">
                                    <i
                                        className={tooltipOptions.iconName}
                                        tabIndex={0}
                                        data-tip
                                        data-for={`title-tooltip-${props.widgetInstanceId}`}
                                    />
                                    {isMounted && (
                                        <ReactTooltip
                                            id={`title-tooltip-${props.widgetInstanceId}`}
                                            textColor="#fff"
                                            backgroundColor={tooltipOptions.backgroundColor}
                                            place="top"
                                            globalEventOff="click"
                                        >
                                            <span
                                                tabIndex={0}
                                                dangerouslySetInnerHTML={{
                                                    __html: tooltipText,
                                                }}
                                            />
                                        </ReactTooltip>
                                    )}
                                </div>
                            )}
                            {showIcon && iconPosition === 'Right' && renderIcon('right')}
                        </div>
                    ) : (
                        <p
                            id="hide-msg-titles"
                            tabIndex={
                                globalDisableTabStaticData || rest.disableTabStaticData ? -1 : 0
                            }
                        >
                            {isRender.hideTitlesMsg}
                        </p>
                    )}
                    {isRender.children ? (
                        <div
                            ref={wrapperRef}
                            className={`wrapper ${
                                showSection ? 'wrapper--show' : 'wrapper--hidden'
                            }`}
                        >
                            {rest.toggleSection ? (
                                <>
                                    <div className="section--top">
                                        <Placeholder
                                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-top`}
                                            parentWidgetInstanceId={widgetInstance?.id}
                                            data={data}
                                        />
                                        <label
                                            className={`switch ${
                                                data?.lang === 'fr' && 'switch-fr'
                                            }`}
                                            data-testid={`toggle-switch`}
                                            onClick={() => {
                                                sections.length > 1 &&
                                                    setToggleSwitch(
                                                        toggleSwitch === 'on' ? 'off' : 'on'
                                                    );
                                                tracking({
                                                    event: 'Toggle Section',
                                                    data: data,
                                                    dynamicEvents: dynamicEvents,
                                                    useAnalytics: useAnalytics,
                                                    toggleSwitch:
                                                        toggleSwitch === 'on'
                                                            ? analyticsLabels?.[0]
                                                            : analyticsLabels?.[1],
                                                    useLowerCase: useLowerCase,
                                                });
                                            }}
                                        >
                                            <span
                                                className={`slider ${
                                                    toggleSwitch === 'on'
                                                        ? 'toggle--on'
                                                        : 'toggle--off'
                                                }`}
                                            >
                                                {sections.map((s: ToggleSection, index: number) => (
                                                    <button
                                                        className={`toggle-label ${
                                                            activeSection.id === s.id
                                                                ? 'active'
                                                                : ''
                                                        }`}
                                                        key={index}
                                                        aria-label={s?.label}
                                                        tabIndex={sections.length <= 1 ? -1 : 0}
                                                    >
                                                        {s?.label}
                                                    </button>
                                                ))}
                                            </span>
                                        </label>
                                    </div>
                                    {sections.map((s: ToggleSection) => (
                                        <div
                                            key={s.id}
                                            className={`toggle-section ${
                                                activeSection.id === s.id ? 'show' : ''
                                            }`}
                                        >
                                            <Placeholder
                                                key={s.id}
                                                contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${s.id}`}
                                                parentWidgetInstanceId={widgetInstance?.id}
                                                data={data}
                                            />
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <Placeholder
                                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                                    parentWidgetInstanceId={widgetInstance?.id}
                                    data={data}
                                />
                            )}
                        </div>
                    ) : (
                        <p id="hide-msg-children">{isRender.hideChildrenMsg}</p>
                    )}
                </section>
            ) : (
                <p id="hide-msg-titled-section">{isRender.hideTitledSectionMsg}</p>
            )}
        </>
    );
};

export default TitledSection;
